import React, { useMemo, useRef }  from 'react';
import { useQuery } from 'react-query';
import { useTranslation } from 'react-i18next';
import { useLiveQuery } from 'dexie-react-hooks';
import { Box, CircularProgress } from '@mui/material';

import { InitialIsAckRequiredDocumentsState, useDocumentActionsStatus } from 'PdfDocumentGrid/hooks/DocumentActionsStatus.context';
import { IDocumentVersion, ISynchronizationItemMeta, getAllSynchronizationItemsMeta } from 'utils/DexieDBManager';
import { styledPdfDocumentsList } from '../PdfDocumentGrid/PdfDocumentsList/stylesPdfDocumentsList';
import { useRedirectIfUnauthenticated } from 'utils/azureAD/useRedirectIfUnauthenticated';
import { getSynchronizationItemsAsync } from 'PdfDocumentGrid/pdfDocuments.service';
import { APPLICABLE_STATE_NAME } from 'utils/DexieDBManager/dexieDB.models';
import { filterAcknowledgementSynchronizationItemsMeta } from './helper';
import PdfDocumentsList from 'PdfDocumentGrid/PdfDocumentsList';
import AlertMessage from 'Shared.Components/AlertMessage';
import { RESOURCES_KEYS } from 'utils/i18n/resources';
import ErrorBoundary from 'utils/ErrorBoundary';
import { computeErrorMessage } from 'utils/utils';
import { isPwa } from 'utils/pwa';

const AcknowledgementPage: React.FunctionComponent = () => {
  const hasRun = useRef(false);
  const { t: translate } = useTranslation();
  const { accounts } = useRedirectIfUnauthenticated();
  const { state, dispatch } = useDocumentActionsStatus();
  const synchronizationItemsFromDb = useLiveQuery<ISynchronizationItemMeta[]>(() => getAllSynchronizationItemsMeta());
  const isOnline = navigator.onLine;
  const username = accounts[0]?.username;

  const { data: getSynchronizationItemsData, isLoading: isGetSynchronizationItemsLoading } = useQuery(
    ["synchronizationItemsWithAcknowledgement", { username, isOnlyAck: true }],
    () => getSynchronizationItemsAsync(username, { isOnlyAck: true }),
    {
      cacheTime: 0, 
      staleTime: 0,
      enabled: isOnline && Boolean(username),
    }
  );

  const filteredSynchronizationItemsFromDb = filterAcknowledgementSynchronizationItemsMeta(synchronizationItemsFromDb || []);
  const synchronizationItemsFromServer = useMemo(() => getSynchronizationItemsData?.responseData?.items || [], [getSynchronizationItemsData?.responseData?.items]);

  const errorPageTitle = translate(RESOURCES_KEYS.ERROR_BOUNDARY_TITLE, {page: translate(RESOURCES_KEYS.MENU_ACKNOWLEDGEMENT)});

  const isDataLoading = (isOnline && isGetSynchronizationItemsLoading && !synchronizationItemsFromServer.length) ||
                        (isPwa &&  !isOnline && !filteredSynchronizationItemsFromDb.length);

  React.useEffect(() => {
    if(isDataLoading) {
      return
    }

    if(hasRun.current) {
      return
    }
  
      if (isPwa && !isOnline) {
        filteredSynchronizationItemsFromDb.forEach((synchronization) => {
  
          const isCategoriesPageInitialized = state.categoriesIsAckRequiredDocumentsState.get(synchronization.id)?.isCategoriesPageInitialized;
          if (isCategoriesPageInitialized) {
            dispatch({
              type: "initCategoriesIsAckRequiredDocumentsState",
              isAckRequiredDocumentsState: {
                ...state.categoriesIsAckRequiredDocumentsState.get(synchronization.id),
                synchronizationId: synchronization.id,
                isCategoriesPageInitialized: false,
              } as InitialIsAckRequiredDocumentsState,
            });
          }

          const isStarredPageInitialized = state.starredIsAckRequiredDocumentsState.get(synchronization.id)?.isStarredPageInitialized;
          if (isStarredPageInitialized) {
            dispatch({
              type: "initStarredIsAckRequiredDocumentsState",
              isAckRequiredDocumentsState: {
                ...state.starredIsAckRequiredDocumentsState.get(synchronization.id),
                synchronizationId: synchronization.id,
                isStarredPageInitialized: false,
              } as InitialIsAckRequiredDocumentsState,
            });
          }
          
          const isAcknowledgementPageInitialized = state.acknowledgementIsAckRequiredDocumentsState.get(synchronization.id)?.isAcknowledgementPageInitialized;
          if (!isAcknowledgementPageInitialized) {
            dispatch({
              type: "initAcknowledgementIsAckRequiredDocumentsState",
              isAckRequiredDocumentsState: {
                isAcknowledgementPageInitialized: true,
                synchronizationId: synchronization.id,
                documentsVersionCount: synchronization.acknowledgementDocumentVersionsCount,
                applicablesDocumentsCount: synchronization.acknowledgementApplicableDocumentsCount,
                nextApplicablesDocumentsCount: synchronization.acknowledgementNextApplicableDocumentsCount,
                isAckRequiredApplicableDocumentsCount: synchronization.acknowledgementIsAckRequiredApplicableDocumentsCount,
                isAckRequiredNextApplicableDocumentsCount: synchronization.acknowledgementIsAckRequiredNextApplicableDocumentsCount,
              },
            });
          }
        });
        hasRun.current = true;
      } else if (isOnline && synchronizationItemsFromServer.length) {
        
        synchronizationItemsFromServer.forEach((synchronization) => {  
  
          const isCategoriesPageInitialized = state.categoriesIsAckRequiredDocumentsState.get(synchronization.id)?.isCategoriesPageInitialized;
          if (isCategoriesPageInitialized) {
            dispatch({
              type: "initCategoriesIsAckRequiredDocumentsState",
              isAckRequiredDocumentsState: {
                ...state.categoriesIsAckRequiredDocumentsState.get(synchronization.id),
                synchronizationId: synchronization.id,
                isCategoriesPageInitialized: false,
              } as InitialIsAckRequiredDocumentsState,
            });
          }
          
          const isStarredPageInitialized = state.starredIsAckRequiredDocumentsState.get(synchronization.id)?.isStarredPageInitialized;
          if (isStarredPageInitialized) {
            dispatch({
              type: "initStarredIsAckRequiredDocumentsState",
              isAckRequiredDocumentsState: {
                ...state.starredIsAckRequiredDocumentsState.get(synchronization.id),
                synchronizationId: synchronization.id,
                isStarredPageInitialized: false,
              } as InitialIsAckRequiredDocumentsState,
            });
          }
          
          const isAcknowledgementPageInitialized = state.acknowledgementIsAckRequiredDocumentsState.get(synchronization.id)?.isAcknowledgementPageInitialized;
          if (!isAcknowledgementPageInitialized) {
            const ackApplicablesCount = synchronization.applicables?.length ?? 0;
            const ackNextApplicablesCount = synchronization.nextApplicables?.length ?? 0;
            dispatch({
              type: "initAcknowledgementIsAckRequiredDocumentsState",
              isAckRequiredDocumentsState: {
                isAcknowledgementPageInitialized: true,
                synchronizationId: synchronization.id,
                applicablesDocumentsCount: ackApplicablesCount,
                nextApplicablesDocumentsCount: ackNextApplicablesCount,
                isAckRequiredApplicableDocumentsCount: ackApplicablesCount,
                isAckRequiredNextApplicableDocumentsCount: ackNextApplicablesCount,
                documentsVersionCount: ackApplicablesCount + ackNextApplicablesCount,
              },
            });
          }
              
          synchronization.applicables.forEach((documentVersion) => {
            const isInitialized =state.documentActionsStatus.get(documentVersion.parentDocument.id)?.isInitialized;
            if (!isInitialized) {
              dispatch({
                type: "init",
                initialDocumentStatus: {
                  isAckRequired: documentVersion.uds.isAck,
                  isStarredDocument: documentVersion.uds.isFav,
                  isUnread: !Boolean(documentVersion.uds.lastRead),
                  parentDocumentId: documentVersion.parentDocument.id,
                  documentVersionType: APPLICABLE_STATE_NAME.APPLICABLE,
                },
              });
            }
          });
      
          synchronization.nextApplicables.forEach((documentVersion) => {
            const isInitialized = Boolean(state.documentActionsStatus.get(documentVersion.parentDocument.id)?.isInitialized);
            if (!isInitialized) {
              dispatch({
                type: "init",
                initialDocumentStatus: {
                  isAckRequired: documentVersion.uds.isAck,
                  isStarredDocument: documentVersion.uds.isFav,
                  isUnread: !Boolean(documentVersion.uds.lastRead),
                  parentDocumentId: documentVersion.parentDocument.id,
                  documentVersionType: APPLICABLE_STATE_NAME.NEXT_APPLICABLE,
                },
              });
            }
          });
        });
        hasRun.current = true;
      }
    
    }, [
      dispatch,
      state, 
      isOnline,
      isDataLoading,
      synchronizationItemsFromServer,
      isGetSynchronizationItemsLoading,
      filteredSynchronizationItemsFromDb
    ]);
  
  
  return (
    <ErrorBoundary title={errorPageTitle}>
    <Box id='acknowledgementPage' sx={styledPdfDocumentsList}>
      {isOnline && isGetSynchronizationItemsLoading && !getSynchronizationItemsData ? (
        <Box sx={{ display: 'flex', width: '100%', justifyContent: 'center' }}>
          <CircularProgress />
        </Box>
      ) : getSynchronizationItemsData?.error ? (
        <AlertMessage title={<h2>{errorPageTitle}</h2>}>
          <h3>{`${translate(computeErrorMessage(getSynchronizationItemsData?.error))}, ${translate(
            RESOURCES_KEYS.PLEASE_CONTACT_SUPPORT_TEAM
          )}.`}</h3>
        </AlertMessage>
      ) : (
        <>
          {!synchronizationItemsFromDb?.length && !synchronizationItemsFromServer.length ? (
            <span className='acknowledgement-page'>
              <AlertMessage severity='info' hasIcon={false}>
                <h2>{translate(RESOURCES_KEYS.ACKNOWLEDGEMENT_EMPTY)}</h2>
              </AlertMessage>
            </span>
          ) : (
            <PdfDocumentsList
              synchronizationItemsFromDb={filteredSynchronizationItemsFromDb}
              synchronizationItemsFromServer={synchronizationItemsFromServer}      
            />
          )}
        </>
      )}
    </Box>
  </ErrorBoundary>
  );
};
export default AcknowledgementPage;

export const getIsAckRequiredDocumentsCount = (documentVersions: IDocumentVersion[]) => {
  return documentVersions.filter((app) => app.uds.isAck).length || 0;
};